import React, { useMemo, useState } from "react";
import { SectionTitle, InputCss } from "../../_parts.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { useBtlUploads } from "../shared/useBtlUploads.js";
import {
  buildUploadLabelMap,
  isNonNegativeNumber,
  isValidEmail,
} from "../shared/formValidation.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import { arePhonesValid, isValidPhone, PHONE_ERROR_MOBILE } from "../../../../utils/validatePhone.js";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const MAX_MEMBERS = 4;

export default function PensionForm({
  onBack,
  onNext,
  initialPayload,
  mode = "wizard",
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("pension", productId || initialPayload?.productId));
  const initial = savedDraft?.btl?.pension || initialPayload?.btl?.pension || {};

  const [scheme, setScheme] = useState({
    name: initial.scheme?.name || "",
    number: initial.scheme?.number || "",
    props: initial.scheme?.props ?? initial.props ?? "",
  });

  const [members, setMembers] = useState(
    initial.members?.length
      ? initial.members
      : [{ id: uid(), firstName: "", surname: "", phone: "", email: "" }]
  );

  const [propertyAddress, setPropertyAddress] = useState(initial.propertyAddress || "");
  const { uploads, uploadingFiles, handleUpload, handleRemove } = useBtlUploads(initial.uploads || {});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const primary = members[0] || {};
  const canAddMore = members.length < MAX_MEMBERS;
  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const updateMember = (id, key, val) =>
    setMembers((arr) => arr.map((m) => (m.id === id ? { ...m, [key]: val } : m)));

  const addMember = () => {
    if (members.length >= MAX_MEMBERS) return;
    setMembers((m) => [...m, { id: uid(), firstName: "", surname: "", phone: "", email: "" }]);
  };

  const removeMember = (id) => {
    if (members.length <= 1) return;
    setMembers((list) => list.filter((m) => m.id !== id));
  };

  const payloadObj = useMemo(
    () => ({
      ...(initialPayload || {}),
      btl: {
        ...(initialPayload?.btl || {}),
        borrowerType: "Pension/Trust",
        pension: { scheme, members, uploads, propertyAddress },
      },
      formData: {
        ...(initialPayload?.formData || {}),
        propertyAddress,
        property_address: propertyAddress,
      },
    }),
    [initialPayload, scheme, members, uploads, propertyAddress]
  );

  const draftKey = getDraftStorageKey("pension", productId || initialPayload?.productId);

  const validateDetails = () => {
    const err = {};
    if (!scheme.name.trim()) err.scheme_name = "Pension/Trust Name is required.";
    if (!primary.firstName?.trim()) err.member_firstName = "Member 1 First Name is required.";
    if (!primary.surname?.trim()) err.member_surname = "Member 1 Surname is required.";
    if (!isValidPhone(primary.phone)) err.member_phone = PHONE_ERROR_MOBILE;
    if (!primary.email?.trim()) err.member_email = "Member 1 Email is required.";
    else if (!isValidEmail(primary.email)) err.member_email = "Enter a valid email address.";
    setFieldErrors(err);
    setTouched(Object.fromEntries(Object.keys(err).map((k) => [k, true])));
    return !Object.keys(err).length;
  };

  const validateProperty = () => {
    const err = {};
    if (!isNonNegativeNumber(scheme.props)) err.scheme_props = "Enter a valid number (0 or more).";
    if (!propertyAddress.trim()) err.propertyAddress = "Security Address is required.";
    setFieldErrors((e) => ({ ...e, ...err }));
    setTouched((t) => ({ ...t, scheme_props: true, propertyAddress: true }));
    return !Object.keys(err).length;
  };

  const stepErrors = useMemo(
    () => ({
      details: Object.keys(fieldErrors).some((k) =>
        ["scheme_name", "member_firstName", "member_surname", "member_phone", "member_email"].includes(k)
      ),
      property: Boolean(fieldErrors.scheme_props || fieldErrors.propertyAddress),
      documents: false,
      review: false,
    }),
    [fieldErrors]
  );

  const validateStep = async (stepIndex) => {
    if (stepIndex === 0) return validateDetails();
    if (stepIndex === 1) return validateProperty();
    return true;
  };

  const finish = (extras = {}) => {
    onNext?.({
      type: "pension",
      scheme,
      members,
      uploads,
      propertyAddress,
      payloadObj,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(), []);

  return (
    <>
      <BorrowerSteppedForm
        title="PENSION/TRUST REGISTRATION FORM"
        subtitle="Please complete all required fields and upload necessary documents"
        draftKey={draftKey}
        draftSnapshot={payloadObj}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        mode={mode}
        detailsContinueDisabled={!arePhonesValid(members.map((m, i) => ({ value: m.phone, required: i === 0 })))}
        renderDetails={() => (
          <div className="space-y-5">
            <div>
              <SectionTitle>Scheme</SectionTitle>
              <FormFieldRow>
                <FormField
                  id="scheme-name"
                  label="Pension/Trust Name"
                  required
                  error={fieldErrors.scheme_name}
                  touched={touched.scheme_name}
                >
                  <input
                    id="scheme-name"
                    className="input w-full"
                    value={scheme.name}
                    onChange={(e) => setScheme((s) => ({ ...s, name: e.target.value }))}
                    onBlur={() => touch("scheme_name")}
                  />
                </FormField>
                <FormField id="scheme-number" label="Pension/Trust Number">
                  <input
                    id="scheme-number"
                    className="input w-full"
                    value={scheme.number}
                    onChange={(e) => setScheme((s) => ({ ...s, number: e.target.value }))}
                  />
                </FormField>
              </FormFieldRow>
            </div>

            <div>
              <SectionTitle>Members</SectionTitle>
              {members.map((m, idx) => (
                <div key={m.id} className="rounded-xl border border-slate-200 p-3 mb-3">
                  <div className="mb-2 flex justify-between items-center">
                    <p className="text-sm font-bold text-slate-900">Member {idx + 1}</p>
                    {members.length > 1 ? (
                      <button
                        type="button"
                        className="text-xs text-red-500"
                        onClick={() => removeMember(m.id)}
                        aria-label={`Remove member ${idx + 1}`}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <FormFieldRow>
                    <FormField
                      id={`member-${m.id}-firstName`}
                      label="First Name"
                      required={idx === 0}
                      error={idx === 0 ? fieldErrors.member_firstName : undefined}
                      touched={idx === 0 ? touched.member_firstName : false}
                    >
                      <input
                        id={`member-${m.id}-firstName`}
                        className="input w-full"
                        value={m.firstName}
                        onChange={(e) => updateMember(m.id, "firstName", e.target.value)}
                        onBlur={() => idx === 0 && touch("member_firstName")}
                      />
                    </FormField>
                    <FormField
                      id={`member-${m.id}-surname`}
                      label="Surname"
                      required={idx === 0}
                      error={idx === 0 ? fieldErrors.member_surname : undefined}
                      touched={idx === 0 ? touched.member_surname : false}
                    >
                      <input
                        id={`member-${m.id}-surname`}
                        className="input w-full"
                        value={m.surname}
                        onChange={(e) => updateMember(m.id, "surname", e.target.value)}
                        onBlur={() => idx === 0 && touch("member_surname")}
                      />
                    </FormField>
                    <FormField id={`member-${m.id}-phone`} label="Phone Number" required={idx === 0}>
                      <PhoneField
                        id={`member-${m.id}-phone`}
                        value={m.phone}
                        required={idx === 0}
                        onChange={(next) => updateMember(m.id, "phone", next)}
                        onBlur={() => idx === 0 && touch("member_phone")}
                      />
                    </FormField>
                    <FormField
                      id={`member-${m.id}-email`}
                      label="Email"
                      required={idx === 0}
                      error={idx === 0 ? fieldErrors.member_email : undefined}
                      touched={idx === 0 ? touched.member_email : false}
                    >
                      <input
                        id={`member-${m.id}-email`}
                        type="email"
                        className="input w-full"
                        value={m.email}
                        onChange={(e) => updateMember(m.id, "email", e.target.value)}
                        onBlur={() => idx === 0 && touch("member_email")}
                      />
                    </FormField>
                  </FormFieldRow>
                </div>
              ))}
              <button
                type="button"
                disabled={!canAddMore}
                onClick={addMember}
                className={`px-4 py-2 rounded-xl text-white text-sm ${
                  canAddMore ? "bg-emerald-500 hover:bg-emerald-600" : "bg-emerald-300 cursor-not-allowed"
                }`}
              >
                + Add Member
              </button>
            </div>
          </div>
        )}
        renderProperty={() => (
          <div>
            <SectionTitle>Property &amp; finance</SectionTitle>
            <FormFieldRow>
              <FormField
                id="scheme-props"
                label="Number of properties owned"
                required
                error={fieldErrors.scheme_props}
                touched={touched.scheme_props}
              >
                <input
                  id="scheme-props"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className="input w-full"
                  value={scheme.props}
                  onChange={(e) => setScheme((s) => ({ ...s, props: e.target.value }))}
                  onBlur={() => touch("scheme_props")}
                />
              </FormField>
              <FormField
                id="pension-propertyAddress"
                label="Security Address"
                required
                error={fieldErrors.propertyAddress}
                touched={touched.propertyAddress}
                className="sm:col-span-2"
              >
                <input
                  id="pension-propertyAddress"
                  className="input w-full"
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  onBlur={() => touch("propertyAddress")}
                />
              </FormField>
            </FormFieldRow>
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel
            uploads={uploads}
            uploadingFiles={uploadingFiles}
            onUpload={handleUpload}
            onRemove={handleRemove}
          />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                rows: [
                  { label: "Scheme name", value: scheme.name },
                  { label: "Scheme number", value: scheme.number || "—" },
                ],
                content: members.map((m, i) => (
                  <p key={m.id} className="text-sm">
                    <span className="text-slate-500">Member {i + 1}: </span>
                    {m.firstName} {m.surname} · {m.phone} · {m.email}
                  </p>
                )),
              },
              {
                stepId: "property",
                stepIndex: 1,
                title: "Property & finance",
                rows: [
                  { label: "Properties owned", value: scheme.props },
                  { label: "Security Address", value: propertyAddress },
                ],
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
